(() => {
  if (customElements.get("purchase-option-component")) return;

  const parseBool = (value) => value === "true" || value === true;

  const normalizeSubscriptionsMode = (value) => {
    const mode = String(value || "Hide").trim().toLowerCase();
    if (mode === "show") return "show";
    if (mode === "mandatory") return "mandatory";
    return "hide";
  };

  const emit = (target, name, detail) => {
    target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
  };

  const formatMoney = (cents) => {
    const amount = (Number(cents) / 100).toFixed(2);
    const fmt = window.Shopify?.money_format || "${{amount}}";
    return fmt
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace(".", ","))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(Number(cents) / 100)))
      .replace(/\{\{\s*amount\s*\}\}/, amount);
  };

  const parseSellingPlanDiscountMap = (raw) => {
    const map = new Map();
    const text = String(raw || "").trim();
    if (!text) return map;

    text.split(",").forEach((segment) => {
      const parts = segment.trim().split("/").map((part) => part.trim());
      if (parts.length < 3) return;
      const [planId, initialKey, recurringKey] = parts;
      if (!planId) return;
      map.set(String(planId), { initialKey, recurringKey });
    });

    return map;
  };

  const parseMainProductDiscountMap = (raw) => {
    const map = new Map();
    const text = String(raw || "").trim();
    if (!text) return map;

    text.split(",").forEach((segment) => {
      const parts = segment.trim().split("/").map((part) => part.trim());
      if (parts.length < 3) return;
      const quantity = parseInt(parts[0], 10);
      if (!quantity || quantity <= 0) return;
      map.set(quantity, {
        oneTime: Number(parts[1]) || 0,
        subscribe: Number(parts[2]) || 0
      });
    });

    return map;
  };

  const computeCompareAtDiscount = (compareAtCents, priceCents) => {
    if (compareAtCents <= 0 || compareAtCents <= priceCents) return 0;
    return Math.round((compareAtCents - priceCents) / compareAtCents * 100);
  };

  const buildCompareAtProperties = (compareAtCents, priceCents) => ({
    _compare_at_price: String(compareAtCents || 0),
    _compare_at_price_discount: String(computeCompareAtDiscount(compareAtCents, priceCents))
  });

  const parsePromotionalSource = (raw) => {
    const text = String(raw ?? "").trim();
    return text || null;
  };

  const discountMultiplier = (percent) => {
    const value = Number(percent) || 0;
    if (value <= 0) return 1;
    return Math.abs(value / 100 - 1);
  };

  const getDefaultMarketDiscounts = (variant) => {
    const discounts = variant?.discounts;
    return discounts && typeof discounts === "object" ? discounts : null;
  };

  const getImplicitDiscountPercent = (originalPrice, compareAtPrice) => {
    const price = Number(originalPrice) || 0;
    const compareAt = Number(compareAtPrice) || 0;
    if (compareAt <= 0 || compareAt <= price) return 0;
    return ((compareAt - price) / compareAt) * 100;
  };

  const ordinal = (index) => {
    const n = index + 1;
    if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
    switch (n % 10) {
      case 1: return `${n}st`;
      case 2: return `${n}nd`;
      case 3: return `${n}rd`;
      default: return `${n}th`;
    }
  };

  class PurchaseOptionComponent extends HTMLElement {
    connectedCallback() {
      this._input = this.querySelector("[data-option-input]");
      if (!this._input) return;

      this._isMultiSelect = parseBool(this.dataset.multiSelect);
      this._multiMax = parseInt(this.dataset.multiSelectMax || "1", 10);
      this._showAddQuantityPicker = parseBool(this.dataset.showAddQuantityPicker);
      this._addonMulti = this.querySelector("[data-purchase-option-addon-multi]");
      this._addonSelect = this.querySelector("[data-purchase-option-addon-select]");
      this._optionForm = this.querySelector("[data-purchase-option-form]");
      this._optionFormOld = this.querySelector("[data-purchase-option-form-old]");
      this._qtyPicker = this.querySelector("[data-addon-qty-picker]");
      this._slotsGrid = this.querySelector("[data-addon-slots-grid]");
      this._cardDropdown = this.querySelector("card-dropdown-component");
      this._subscriptionsMode = normalizeSubscriptionsMode(this.dataset.subscriptionsMode);
      this._showSellingPlansSelector = parseBool(this.dataset.showSellingPlansSelector);
      this._subscribeLabel = this.dataset.subscriptionText || "Subscribe & Save";
      this._onceLabel = this.dataset.oneTimeText || "Buy Once";
      this._purchaseToggle = this.querySelector("[data-purchase-option-toggle]");
      this._subscriptionControls = this.querySelector("[data-purchase-option-subscription-controls]");
      this._subscriptionDisclaimer = this.querySelector("[data-purchase-option-subscription-disclaimer]");
      this._sellingPlanSelectWrap = this.querySelector("[data-purchase-option-selling-plan-select]");
      this._sellingPlanDropdown = this._sellingPlanSelectWrap?.querySelector("dropdown-component") || null;
      this._sellingPlanMenu = this._sellingPlanSelectWrap?.querySelector("[data-selling-plan-menu]") || null;
      this._sellingPlanInput = this._sellingPlanSelectWrap?.querySelector("[data-selling-plan-input]") || null;
      this._sellingPlanValueEl = this._sellingPlanDropdown?.querySelector(".dropdown__value") || null;
      this._togglePill = this._purchaseToggle?.querySelector("[data-toggle-pill]");
      this._purchaseType = "once";
      this._sellingPlanId = "";
      this._pendingSlotPurchaseType = "once";

      this._mainProductDiscountMap = parseMainProductDiscountMap(this.dataset.mainProductDiscountMap);
      this._loadVariantData();

      if (this._subscriptionsMode === "show" && this._purchaseToggle) {
        this._initPurchaseToggle();
      }

      if (this._showSellingPlansSelector && this._sellingPlanSelectWrap) {
        this._initSellingPlanSelect();
      }

      this._onSellingPlanChange = this._handleSellingPlanChange.bind(this);
      this.addEventListener("dropdown:change", this._onSellingPlanChange);

      if (this._isMultiSelect && this._addonMulti) {
        this._initMultiSelect();
      } else {
        this._selectedVariantId = this.querySelector("[data-purchase-option-addon-input]")?.value || "";
        this._cardDropdown?.addEventListener("dropdown:change", this._handleAddonChange.bind(this));
      }

      this._onChange = this._handleChange.bind(this);
      this._input.addEventListener("change", this._onChange);
      this._handleChange();
    }

    disconnectedCallback() {
      this._input?.removeEventListener("change", this._onChange);
      this.removeEventListener("dropdown:change", this._onSellingPlanChange);
    }

    get optionId() {
      return this.dataset.optionId || "";
    }

    get selected() {
      return this._input?.checked ?? false;
    }

    set selected(value) {
      const next = Boolean(value);
      if (this._input) this._input.checked = next;
      if (!next) this.clearSelection();
      else this._syncAddonVisibility();
    }

    get selectedVariantId() {
      if (!this.selected) return "";
      if (this._isMultiSelect) return this.selectedVariantIds[0] || "";
      return this._selectedVariantId || "";
    }

    get selectedVariantIds() {
      if (!this.selected || !this._isMultiSelect) return [];
      return this._slotSelections
        .slice(0, this._currentQty)
        .map((selection) => (selection?.id ? String(selection.id) : ""))
        .filter(Boolean);
    }

    get shouldPreselect() {
      return parseBool(this.dataset.preselect) || parseBool(this._addonMulti?.dataset.preselect);
    }

    clearSelection() {
      if (this._isMultiSelect) {
        this._slotSelections = Array.from({ length: this._multiMax }, () => null);
        this._currentQty = this._multiMax;
        this._activeSlotIndex = 0;
        this._syncQtyPicker();
        this._renderAllSlots();
        this._syncDropdownForActiveSlot();
      } else {
        this._selectedVariantId = "";
        this._sellingPlanId = "";
        const addonInput = this.querySelector("[data-purchase-option-addon-input]");
        if (addonInput) addonInput.value = "";
      }
      this._purchaseType = "once";
      this._pendingSlotPurchaseType = "once";
      this._syncPurchaseToggleUi();
      this._syncAddonVisibility();
    }

    _loadVariantData() {
      const variantsJson = this.querySelector("[data-addon-variants-json]");
      const rawVariants = variantsJson ? JSON.parse(variantsJson.textContent) : [];
      this._variants = rawVariants.map((variant) => ({
        ...variant,
        sellingPlanDiscountMap: parseSellingPlanDiscountMap(variant.sellingPlanDiscountMap || "")
      }));
      this._variantMap = new Map(this._variants.map((variant) => [String(variant.id), variant]));
    }

    _getVariantPlanMap(variant) {
      return variant?.sellingPlanDiscountMap instanceof Map ? variant.sellingPlanDiscountMap : new Map();
    }

    _regularPriceFallback(variant) {
      const originalPrice = Number(variant?.price) || 0;
      const compareAtPrice = Number(variant?.compareAtPrice) || 0;

      return {
        discountedPrice: originalPrice,
        originalPrice,
        compareAtPrice,
        showCompare: compareAtPrice > originalPrice
      };
    }

    _buildPricingResult(originalPrice, compareAtPrice, metafieldPercent) {
      const implicitPercent = getImplicitDiscountPercent(originalPrice, compareAtPrice);
      const effectivePercent = Math.max(Number(metafieldPercent) || 0, implicitPercent);

      if (effectivePercent <= 0) {
        return {
          discountedPrice: originalPrice,
          originalPrice,
          compareAtPrice,
          showCompare: compareAtPrice > originalPrice
        };
      }

      const base = compareAtPrice > 0 ? compareAtPrice : originalPrice;
      const calculated = Math.round(base * discountMultiplier(effectivePercent));
      const discountedPrice =
        compareAtPrice > originalPrice ? Math.min(calculated, originalPrice) : calculated;

      return {
        discountedPrice,
        originalPrice,
        compareAtPrice,
        showCompare: (compareAtPrice > 0 ? compareAtPrice : originalPrice) > discountedPrice
      };
    }

    _computeOneTimePricing(variant) {
      const originalPrice = Number(variant?.price) || 0;
      const compareAtPrice = Number(variant?.compareAtPrice) || 0;
      const discounts = getDefaultMarketDiscounts(variant);
      const oneTimePercent = Number(discounts?.oneTime) || 0;

      if (!discounts || oneTimePercent <= 0) {
        return this._buildPricingResult(originalPrice, compareAtPrice, 0);
      }

      return this._buildPricingResult(originalPrice, compareAtPrice, oneTimePercent);
    }

    _computeSubscribePricing(variant, sellingPlanId = "") {
      const originalPrice = Number(variant?.price) || 0;
      const compareAtPrice = Number(variant?.compareAtPrice) || 0;
      const resolvedPlanId = String(sellingPlanId || this._getActiveSellingPlanId() || this._getFirstPlanId(variant.id) || "");
      const mapping = resolvedPlanId ? this._getVariantPlanMap(variant).get(resolvedPlanId) : null;

      if (!mapping?.initialKey) {
        return this._buildPricingResult(originalPrice, compareAtPrice, 0);
      }

      const discounts = getDefaultMarketDiscounts(variant);
      const initialPercent = Number(discounts?.[mapping.initialKey]) || 0;

      if (!discounts) {
        return this._buildPricingResult(originalPrice, compareAtPrice, 0);
      }

      return this._buildPricingResult(originalPrice, compareAtPrice, initialPercent);
    }

    _computeVariantPricing(variant, { purchaseType = "once", sellingPlanId = "" } = {}) {
      const usesPlan = purchaseType === "subscribe" || this._subscriptionsMode === "mandatory";
      if (!usesPlan) {
        return this._computeOneTimePricing(variant);
      }

      return this._computeSubscribePricing(variant, sellingPlanId);
    }

    _applyPricingToSelection(selection, pricing) {
      selection.discountedPrice = pricing.discountedPrice;
      selection.originalPrice = pricing.originalPrice;
      selection.compareAtPrice = pricing.compareAtPrice;
    }

    _pricingSummary(pricing) {
      return {
        discountedPrice: pricing.discountedPrice,
        originalPrice: pricing.originalPrice,
        compareAtPrice: pricing.compareAtPrice
      };
    }

    _pricingTotals(pricing) {
      return pricing.items.reduce(
        (totals, item) => ({
          discountedPrice: totals.discountedPrice + (Number(item.discountedPrice) || 0),
          originalPrice: totals.originalPrice + (Number(item.originalPrice) || 0),
          compareAtPrice: totals.compareAtPrice + (Number(item.compareAtPrice) || 0)
        }),
        { discountedPrice: 0, originalPrice: 0, compareAtPrice: 0 }
      );
    }

    _getDisplayPricingContext() {
      const usesPlan = this._usesSellingPlan();
      return {
        purchaseType: usesPlan ? "subscribe" : this._getActivePurchaseType(),
        sellingPlanId: usesPlan ? this._getActiveSellingPlanId() : ""
      };
    }

    _getAddonQuantity() {
      if (!this.selected) return 0;
      if (this._isMultiSelect) {
        let count = 0;
        for (let i = 0; i < this._currentQty; i += 1) {
          if (this._slotSelections[i]?.id) count += 1;
        }
        return count;
      }
      return this._selectedVariantId ? 1 : 0;
    }

    _resolveAddonPurchaseType() {
      if (this._subscriptionsMode === "mandatory") return "subscribe";
      if (this._subscriptionsMode === "hide") return "once";

      if (this._isMultiSelect) {
        let hasSubscribe = false;
        let hasOnce = false;
        for (let i = 0; i < this._currentQty; i += 1) {
          const selection = this._slotSelections[i];
          if (!selection?.id) continue;
          if (selection.purchaseType === "subscribe") hasSubscribe = true;
          else hasOnce = true;
        }
        if (hasSubscribe && !hasOnce) return "subscribe";
        return "once";
      }

      return this._purchaseType === "subscribe" ? "subscribe" : "once";
    }

    _getMainProductDiscountContext() {
      if (this.dataset.optionType === "lead_form") {
        const overridePercent = Number(
          this._optionFormOld?.dataset.mainProductDiscountOverride ||
            this.dataset.mainProductDiscountOverride ||
            0
        );
        if (!(overridePercent > 0)) return null;
        return {
          addonQuantity: 1,
          purchaseType: "once",
          discountPercent: overridePercent,
          oneTimePercent: overridePercent,
          subscribePercent: overridePercent
        };
      }

      const addonQuantity = this._getAddonQuantity();
      if (!addonQuantity || !this._mainProductDiscountMap?.size) return null;

      const entry = this._mainProductDiscountMap.get(addonQuantity);
      if (!entry) return null;

      const purchaseType = this._resolveAddonPurchaseType();
      const discountPercent = purchaseType === "subscribe" ? entry.subscribe : entry.oneTime;

      return {
        addonQuantity,
        purchaseType,
        discountPercent: Number(discountPercent) || 0,
        oneTimePercent: Number(entry.oneTime) || 0,
        subscribePercent: Number(entry.subscribe) || 0
      };
    }

    _updateAddonPriceElement(priceEl, pricing) {
      if (!priceEl) return;

      const currentEl = priceEl.querySelector("[data-addon-price-current]");
      const compareEl = priceEl.querySelector("[data-addon-price-compare]");
      if (!currentEl) return;

      currentEl.textContent = formatMoney(pricing.discountedPrice);

      if (compareEl) {
        const compareDisplay =
          pricing.compareAtPrice > pricing.discountedPrice
            ? pricing.compareAtPrice
            : pricing.originalPrice > pricing.discountedPrice
              ? pricing.originalPrice
              : 0;

        if (pricing.showCompare && compareDisplay > 0) {
          compareEl.textContent = formatMoney(compareDisplay);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = "";
          compareEl.hidden = true;
        }
      }
    }

    _syncAddonPrices() {
      if (!this._variants.length) return null;

      const displayContext = this._getDisplayPricingContext();
      const pricing = this._getSelectionPricing();

      this._cardDropdown?.querySelectorAll(".card-dropdown__option").forEach((option) => {
        const variantId = option.dataset.variantId || option.dataset.value || "";
        const variant = this._variantMap.get(String(variantId));
        if (!variant) return;

        const optionPricing = this._computeVariantPricing(variant, displayContext);
        this._updateAddonPriceElement(option.querySelector("[data-addon-price]"), optionPricing);
      });

      const selectedVariantId = this._isMultiSelect
        ? this._getActiveSlotVariantId()
        : this._selectedVariantId;

      if (selectedVariantId && this._cardDropdown) {
        this._setDropdownVariant(selectedVariantId);
      }

      this._emitPriceChange(this.selected ? pricing : null);
      return pricing;
    }

    _getSelectionPricing() {
      const items = [];

      if (this._isMultiSelect) {
        for (let i = 0; i < this._currentQty; i += 1) {
          const selection = this._slotSelections[i];
          if (!selection?.id) continue;

          const variant = this._variantMap.get(String(selection.id));
          if (!variant) continue;

          const purchaseType =
            selection.purchaseType ||
            (this._subscriptionsMode === "mandatory" ? "subscribe" : "once");
          const sellingPlanId =
            purchaseType === "subscribe" || this._subscriptionsMode === "mandatory"
              ? selection.sellingPlanId || this._getFirstPlanId(selection.id)
              : "";

          const pricing = this._computeVariantPricing(variant, { purchaseType, sellingPlanId });
          this._applyPricingToSelection(selection, pricing);

          items.push({
            slotIndex: i + 1,
            variantId: String(selection.id),
            ...this._pricingSummary(pricing)
          });
        }
      } else if (this.selected && this._selectedVariantId) {
        const variant = this._variantMap.get(String(this._selectedVariantId));
        if (variant) {
          const purchaseType = this._usesSellingPlan() ? "subscribe" : this._purchaseType || "once";
          const sellingPlanId = this._usesSellingPlan() ? this._getActiveSellingPlanId() : "";
          const pricing = this._computeVariantPricing(variant, { purchaseType, sellingPlanId });
          items.push({
            variantId: String(this._selectedVariantId),
            ...this._pricingSummary(pricing)
          });
        }
      }

      const totalDiscountedCents = items.reduce((sum, item) => sum + (item.discountedPrice || 0), 0);
      return { items, totalDiscountedCents };
    }

    _emitPriceChange(pricing) {
      if (!this.selected) return;
      const totals = pricing || this._getSelectionPricing();
      const pricingTotals = this._isMultiSelect
        ? this._pricingTotals(totals)
        : this._pricingSummary(totals.items[0] || {});

      emit(this, "purchase-option:price-change", {
        optionId: this.optionId,
        selected: this.selected,
        discountedPrice: pricingTotals.discountedPrice,
        originalPrice: pricingTotals.originalPrice,
        compareAtPrice: pricingTotals.compareAtPrice,
        mainProductDiscount: this._getMainProductDiscountContext()
      });
    }

    _shouldShowSellingPlanSelector() {
      if (!this._showSellingPlansSelector || !this.selected) return false;
      if (this._subscriptionsMode === "hide") return false;
      if (this._subscriptionsMode === "mandatory") return true;
      return this._subscriptionsMode === "show" && this._getActivePurchaseType() === "subscribe";
    }

    _shouldShowSubscriptionDisclaimer() {
      if (!this._subscriptionDisclaimer || !this.selected || this._subscriptionsMode === "hide") {
        return false;
      }
      if (this._subscriptionsMode === "mandatory") return true;
      return this._getActivePurchaseType() === "subscribe";
    }

    _syncSubscriptionDisclaimerVisibility() {
      if (!this._subscriptionDisclaimer) return;
      this._subscriptionDisclaimer.hidden = !this._shouldShowSubscriptionDisclaimer();
    }

    _syncSellingPlanSelectorUi() {
      if (!this._showSellingPlansSelector || !this._sellingPlanSelectWrap) {
        this._syncSubscriptionDisclaimerVisibility();
        return;
      }

      const shouldShow = this._shouldShowSellingPlanSelector();
      this._sellingPlanSelectWrap.hidden = !shouldShow;

      if (shouldShow) {
        const variantId = this._getActiveVariantIdForPlans();
        const preferredPlanId = this._getActiveSellingPlanId();
        this._syncSellingPlanSelectorForVariant(variantId, preferredPlanId);
      }

      this._syncSubscriptionDisclaimerVisibility();
    }

    _syncSubscriptionControlsVisibility() {
      const showControls = this.selected && this._subscriptionsMode !== "hide";

      if (this._subscriptionControls) {
        this._subscriptionControls.hidden = !showControls;
      }

      if (this._purchaseToggle) {
        const showToggle = showControls && this._subscriptionsMode === "show";
        this._purchaseToggle.hidden = !showToggle;

        if (!showControls) {
          if (this._isMultiSelect) {
            const selection = this._slotSelections[this._activeSlotIndex];
            if (selection) {
              selection.purchaseType = "once";
              selection.sellingPlanId = "";
            }
            this._pendingSlotPurchaseType = "once";
          } else if (this._purchaseType === "subscribe") {
            this._purchaseType = "once";
            this._sellingPlanId = "";
          }
        }

        if (showToggle) this._syncPurchaseToggleUi();
      }

      this._syncSellingPlanSelectorUi();
      this._syncSubscriptionDisclaimerVisibility();
    }

    _initSellingPlanSelect() {
      // Selling plan changes are handled via delegated listener in connectedCallback.
    }

    _handleSellingPlanChange(event) {
      const dropdown = event.target.closest("dropdown-component");
      if (!dropdown || !this._sellingPlanSelectWrap?.contains(dropdown)) return;

      event.stopPropagation();

      const planId = String(event.detail?.value || this._sellingPlanInput?.value || "");
      if (!planId || !this.selected) return;

      if (this._isMultiSelect) {
        const selection = this._slotSelections[this._activeSlotIndex];
        if (selection?.id) selection.sellingPlanId = planId;
      } else {
        this._sellingPlanId = planId;
      }

      this._syncAddonPrices();
      this._syncSubscriptionDisclaimerVisibility();
      this._emitChange();
    }

    _getVariantPlans(variantId) {
      const variant = this._variantMap.get(String(variantId));
      return Array.isArray(variant?.plans) ? variant.plans : [];
    }

    _getFirstPlanId(variantId) {
      const plans = this._getVariantPlans(variantId);
      return plans[0]?.id ? String(plans[0].id) : "";
    }

    _getActiveVariantIdForPlans() {
      if (this._isMultiSelect) {
        const selection = this._slotSelections[this._activeSlotIndex];
        if (selection?.id) return String(selection.id);
        return this._getDefaultVariantForSlot(this._activeSlotIndex);
      }

      if (this._selectedVariantId) return String(this._selectedVariantId);
      const input = this.querySelector("[data-purchase-option-addon-input]");
      return input?.value ? String(input.value) : "";
    }

    _getActiveSellingPlanId() {
      if (this._isMultiSelect) {
        const selection = this._slotSelections[this._activeSlotIndex];
        if (selection?.sellingPlanId) return String(selection.sellingPlanId);
      } else if (this._sellingPlanId) {
        return String(this._sellingPlanId);
      }

      const inputValue = this._sellingPlanInput?.value;
      return inputValue ? String(inputValue) : "";
    }

    _setSellingPlanDropdownSelection(planId, plans) {
      if (!this._sellingPlanMenu || !this._sellingPlanInput || !this._sellingPlanValueEl) return;

      const id = String(planId || "");
      const plan = plans.find((entry) => String(entry.id) === id) || plans[0];
      if (!plan) {
        this._sellingPlanInput.value = "";
        this._sellingPlanValueEl.textContent = "";
        return;
      }

      this._sellingPlanInput.value = String(plan.id);
      this._sellingPlanValueEl.textContent = plan.label || "";

      this._sellingPlanMenu.querySelectorAll(".dropdown__option").forEach((option) => {
        const selected = option.dataset.value === String(plan.id);
        option.classList.toggle("dropdown__option--selected", selected);
        option.setAttribute("aria-selected", selected ? "true" : "false");
      });
    }

    _rebuildSellingPlanDropdown(variantId, preferredPlanId = "") {
      if (!this._showSellingPlansSelector || !this._sellingPlanSelectWrap) return "";

      const resolvedVariantId = String(variantId || "");
      const plans = resolvedVariantId ? this._getVariantPlans(resolvedVariantId) : [];
      const trigger = this._sellingPlanDropdown?.querySelector(".dropdown__trigger");

      this._sellingPlanSelectWrap.hidden = !this._shouldShowSellingPlanSelector();

      if (!this._shouldShowSellingPlanSelector() || !this._sellingPlanMenu) {
        if (trigger) trigger.disabled = true;
        return "";
      }

      if (!resolvedVariantId || !plans.length) {
        if (trigger) trigger.disabled = true;
        return "";
      }

      if (trigger) trigger.disabled = false;

      const preferred = String(preferredPlanId || "");
      const selectedId = plans.some((plan) => String(plan.id) === preferred)
        ? preferred
        : String(plans[0].id);

      this._sellingPlanMenu.innerHTML = plans
        .map(
          (plan) =>
            `<li class="dropdown__option${String(plan.id) === selectedId ? " dropdown__option--selected" : ""}" role="option" tabindex="-1" data-value="${String(plan.id).replace(/"/g, "&quot;")}" aria-selected="${String(plan.id) === selectedId ? "true" : "false"}">${(plan.label || "").replace(/</g, "&lt;")}</li>`
        )
        .join("");

      this._setSellingPlanDropdownSelection(selectedId, plans);
      return selectedId;
    }

    _syncSellingPlanSelectorForVariant(variantId, preferredPlanId = "") {
      const resolvedVariantId = variantId || this._getActiveVariantIdForPlans();
      const planId = this._rebuildSellingPlanDropdown(resolvedVariantId, preferredPlanId) || "";

      if (!planId) return "";

      if (this._isMultiSelect) {
        const selection = this._slotSelections[this._activeSlotIndex];
        if (selection?.id) selection.sellingPlanId = planId;
      } else {
        this._sellingPlanId = planId;
      }

      this._syncAddonPrices();
      return planId;
    }

    _initPurchaseToggle() {
      this._purchaseToggle.addEventListener("click", (event) => {
        const btn = event.target.closest("[data-toggle]");
        if (!btn || !this._purchaseToggle.contains(btn)) return;
        if (btn.classList.contains("product-purchase-panel__toggle-btn--active")) return;

        const isSubscribe = btn.getAttribute("data-toggle") === "subscribe";

        if (this._isMultiSelect) {
          const selection = this._slotSelections[this._activeSlotIndex];
          const purchaseType = isSubscribe ? "subscribe" : "once";
          if (selection?.id) {
            selection.purchaseType = purchaseType;
          } else {
            this._pendingSlotPurchaseType = purchaseType;
          }
        } else {
          this._purchaseType = isSubscribe ? "subscribe" : "once";
        }

        this._syncSellingPlans();
        this._syncSubscriptionUi();
        this._syncAddonPrices();
        this._emitChange();
      });
    }

    _getActivePurchaseType() {
      if (!this._isMultiSelect) return this._purchaseType;
      const selection = this._slotSelections[this._activeSlotIndex];
      return selection?.purchaseType || this._pendingSlotPurchaseType || "once";
    }

    _getActiveSlotVariantId() {
      const selection = this._slotSelections[this._activeSlotIndex];
      if (selection?.id) return String(selection.id);
      const input = this._cardDropdown?.querySelector("[data-purchase-option-addon-input]");
      return input?.value || "";
    }

    _syncPurchaseToggleUi() {
      if (!this._purchaseToggle) return;

      const isSubscribe = this._getActivePurchaseType() === "subscribe";
      this._purchaseToggle.querySelectorAll("[data-toggle]").forEach((btn) => {
        const active = (btn.getAttribute("data-toggle") === "subscribe") === isSubscribe;
        btn.classList.toggle("product-purchase-panel__toggle-btn--active", active);
        btn.setAttribute("aria-pressed", active ? "true" : "false");
      });
      this._togglePill?.classList.toggle("product-purchase-panel__toggle-pill--right", !isSubscribe);
      this._syncSubscriptionDisclaimerVisibility();
    }

    _getFirstSellingPlanId(variantId) {
      const variant = this._variantMap.get(String(variantId));
      if (!variant) return "";
      return variant.firstPlanId || "";
    }

    _variantHasSellingPlans(variantId) {
      const variant = this._variantMap.get(String(variantId));
      return Boolean(variant?.hasSellingPlans && variant?.firstPlanId);
    }

    _selectionHasSellingPlans() {
      if (this._isMultiSelect) {
        for (let i = 0; i < this._currentQty; i += 1) {
          const selection = this._slotSelections[i];
          if (selection?.id && this._variantHasSellingPlans(selection.id)) return true;
        }
        return false;
      }
      return this._variantHasSellingPlans(this.selectedVariantId);
    }

    _syncSellingPlans() {
      if (this._subscriptionsMode === "hide") {
        if (this._isMultiSelect) {
          for (let i = 0; i < this._currentQty; i += 1) {
            const selection = this._slotSelections[i];
            if (!selection) continue;
            selection.purchaseType = "once";
            selection.sellingPlanId = "";
          }
        } else {
          this._purchaseType = "once";
          this._sellingPlanId = "";
        }
        return;
      }

      if (this._subscriptionsMode === "mandatory") {
        if (this._isMultiSelect) {
          for (let i = 0; i < this._currentQty; i += 1) {
            const selection = this._slotSelections[i];
            if (!selection?.id) continue;
            selection.purchaseType = "subscribe";
            if (!selection.sellingPlanId) {
              selection.sellingPlanId = this._getFirstPlanId(selection.id);
            }
          }
        } else if (this.selectedVariantId) {
          this._purchaseType = "subscribe";
          if (!this._sellingPlanId) {
            this._sellingPlanId = this._getFirstPlanId(this.selectedVariantId);
          }
        }
        return;
      }

      if (this._isMultiSelect) {
        for (let i = 0; i < this._currentQty; i += 1) {
          const selection = this._slotSelections[i];
          if (!selection?.id) continue;
          if (selection.purchaseType === "subscribe") {
            if (!selection.sellingPlanId || !this._showSellingPlansSelector) {
              selection.sellingPlanId = this._getFirstPlanId(selection.id);
            }
          } else {
            selection.sellingPlanId = "";
          }
        }
        return;
      }

      if (this._purchaseType !== "subscribe") {
        this._sellingPlanId = "";
        return;
      }

      if (!this._sellingPlanId || !this._showSellingPlansSelector) {
        this._sellingPlanId = this._getFirstPlanId(this.selectedVariantId);
      }
    }

    _syncSubscriptionUi() {
      this._syncSubscriptionControlsVisibility();
    }

    get selectionItem() {
      if (!this.selected || !this.optionId) return null;

      const promotionalSource = parsePromotionalSource(this.dataset.promotionalSource);
      const optionType = this.dataset.optionType || "";
      if (optionType === "form_purchase_option" || optionType === "lead_form") {
        return {
          optionId: this.optionId,
          selected: true,
          optionType,
          variantId: "",
          discountedPrice: null,
          originalPrice: null,
          compareAtPrice: null,
          mainProductDiscount: this._getMainProductDiscountContext(),
          ...(promotionalSource ? { promotionalSource } : {})
        };
      }

      const pricing = this._getSelectionPricing();
      const mainProductDiscount = this._getMainProductDiscountContext();

      if (this._isMultiSelect) {
        const addonSlots = this._slotSelections
          .slice(0, this._currentQty)
          .map((selection, index) => ({
            slotIndex: index + 1,
            variantId: selection?.id ? String(selection.id) : "",
            purchaseType: selection?.purchaseType || "once",
            sellingPlanId:
              selection?.purchaseType === "subscribe" || this._subscriptionsMode === "mandatory"
                ? selection?.sellingPlanId || ""
                : "",
            discountedPrice: selection?.discountedPrice ?? null,
            originalPrice: selection?.originalPrice ?? null,
            compareAtPrice: selection?.compareAtPrice ?? null
          }))
          .filter((slot) => slot.variantId);

        return {
          optionId: this.optionId,
          selected: true,
          quantity: this._currentQty,
          variantIds: addonSlots.map((slot) => slot.variantId),
          addonSlots,
          mainProductDiscount,
          ...(promotionalSource ? { promotionalSource } : {}),
          ...this._pricingTotals(pricing)
        };
      }

      const pricedItem = pricing.items[0] || null;

      const item = {
        optionId: this.optionId,
        selected: true,
        variantId: this.selectedVariantId,
        discountedPrice: pricedItem?.discountedPrice ?? null,
        originalPrice: pricedItem?.originalPrice ?? null,
        compareAtPrice: pricedItem?.compareAtPrice ?? null,
        mainProductDiscount,
        ...(promotionalSource ? { promotionalSource } : {})
      };

      if (this._subscriptionsMode === "show") {
        item.purchaseType = this._purchaseType;
        item.sellingPlanId =
          this._purchaseType === "subscribe" && this._usesSellingPlan()
            ? this._sellingPlanId
            : "";
      } else if (this._subscriptionsMode === "mandatory") {
        item.purchaseType = "subscribe";
        item.sellingPlanId = this._usesSellingPlan() ? this._sellingPlanId : "";
      }

      return item;
    }

    _usesSellingPlan() {
      if (this._subscriptionsMode === "hide") return false;
      if (this._subscriptionsMode === "mandatory") return true;
      return this._purchaseType === "subscribe";
    }

    _slotSelectionDefaults(variant) {
      if (this._subscriptionsMode === "mandatory") {
        return {
          ...variant,
          purchaseType: "subscribe",
          sellingPlanId: this._getFirstPlanId(variant.id)
        };
      }
      return { ...variant, purchaseType: "once", sellingPlanId: "" };
    }

    _initMultiSelect() {
      if (this._addonMulti?.dataset.subscriptionText) {
        this._subscribeLabel = this._addonMulti.dataset.subscriptionText;
      }
      if (this._addonMulti?.dataset.oneTimeText) {
        this._onceLabel = this._addonMulti.dataset.oneTimeText;
      }
      if (this._addonMulti?.dataset.subscriptionsMode) {
        this._subscriptionsMode = normalizeSubscriptionsMode(this._addonMulti.dataset.subscriptionsMode);
      }
      if (this._addonMulti?.dataset.showSellingPlansSelector) {
        this._showSellingPlansSelector = parseBool(this._addonMulti.dataset.showSellingPlansSelector);
      }

      this._multiMax = parseInt(this._addonMulti.dataset.multiSelectMax || this._multiMax, 10);
      this._currentQty = this._showAddQuantityPicker
        ? this._readQtyValue() || this._multiMax
        : this._multiMax;
      this._activeSlotIndex = 0;
      this._slotSelections = Array.from({ length: this._multiMax }, () => null);

      this.querySelectorAll("[data-addon-slot]").forEach((slotEl, index) => {
        const variantId = slotEl.dataset.variantId || "";
        if (variantId) {
          const variant = this._variantMap.get(variantId);
          if (variant) {
            this._slotSelections[index] = this._slotSelectionDefaults(variant);
          } else {
            this._slotSelections[index] = {
              id: variantId,
              title: slotEl.querySelector(".purchase-options__addon-slot-label")?.textContent?.trim() || "",
              imageUrl: slotEl.querySelector(".oil-selection-card__image")?.getAttribute("src") || "",
              imageWrapStyle: slotEl.querySelector(".oil-selection-card__image-wrap")?.getAttribute("style") || "",
              purchaseType: "once",
              sellingPlanId: ""
            };
          }
        }
      });

      this._addonMulti.addEventListener("click", this._handleMultiClick.bind(this));
      this._cardDropdown?.addEventListener("dropdown:change", this._handleMultiDropdownChange.bind(this));
      this._qtyPicker?.addEventListener("click", this._handleQtyClick.bind(this));

      this._syncQtyPicker();
      this._syncSlotVisibility();
      this._setActiveSlot(this._findFirstEmptySlotIndex());
      this._syncDropdownForActiveSlot();
      this._syncSubscriptionUi();
    }

    _hasAnySlotSelection() {
      for (let i = 0; i < this._currentQty; i += 1) {
        if (this._slotSelections[i]?.id) return true;
      }
      return false;
    }

    _applyPreselectSlots() {
      const used = new Set();
      for (let i = 0; i < this._currentQty; i += 1) {
        const variant = this._variants.find(
          (entry) => entry.available !== false && !used.has(String(entry.id))
        );
        if (!variant) break;
        this._slotSelections[i] = this._slotSelectionDefaults(variant);
        used.add(String(variant.id));
      }
      this._renderAllSlots();
      this._syncSellingPlans();
      this._syncSubscriptionUi();
      this._syncAddonPrices();
    }

    _getUsedVariantIds(excludeIndex = -1) {
      const used = new Set();
      for (let i = 0; i < this._currentQty; i += 1) {
        if (i === excludeIndex) continue;
        const id = this._slotSelections[i]?.id;
        if (id) used.add(String(id));
      }
      return used;
    }

    _getDefaultVariantForSlot(slotIndex) {
      const current = this._slotSelections[slotIndex];
      if (current?.id) return String(current.id);

      const used = this._getUsedVariantIds(slotIndex);
      const next = this._variants.find(
        (entry) => entry.available !== false && !used.has(String(entry.id))
      );
      if (next) return String(next.id);

      const fallback = this._variants.find((entry) => entry.available !== false);
      return fallback ? String(fallback.id) : this._getFirstAvailableDropdownVariantId();
    }

    _getFirstAvailableDropdownVariantId() {
      const options = this._cardDropdown?.querySelectorAll(".card-dropdown__option") || [];
      for (const option of options) {
        if (option.dataset.available !== "false") return option.dataset.variantId || option.dataset.value || "";
      }
      const first = options[0];
      return first ? first.dataset.variantId || first.dataset.value || "" : "";
    }

    _syncDropdownForActiveSlot() {
      if (!this.selected || !this._cardDropdown) return;
      const variantId = this._getDefaultVariantForSlot(this._activeSlotIndex);
      this._setDropdownVariant(variantId);
      this._syncSubscriptionUi();
      this._syncAddonPrices();
    }

    _findFirstEmptySlotIndex() {
      for (let i = 0; i < this._currentQty; i += 1) {
        if (!this._slotSelections[i]) return i;
      }
      return 0;
    }

    _handleMultiClick(event) {
      const removeBtn = event.target.closest("[data-addon-slot-remove]");
      if (removeBtn) {
        event.preventDefault();
        event.stopPropagation();
        const slotEl = removeBtn.closest("[data-addon-slot]");
        if (!slotEl) return;
        const index = parseInt(slotEl.dataset.slotIndex, 10) - 1;
        this._clearSlot(index);
        return;
      }

      const slotTrigger = event.target.closest("[data-addon-slot-trigger]");
      if (!slotTrigger) return;
      const slotEl = slotTrigger.closest("[data-addon-slot]");
      if (!slotEl || slotEl.hidden) return;
      const index = parseInt(slotEl.dataset.slotIndex, 10) - 1;
      this._setActiveSlot(index);
      this._syncDropdownForActiveSlot();
    }

    _handleMultiDropdownChange(event) {
      const variantId = event.detail.variantId || "";
      if (!variantId || !this.selected) return;

      const variant = this._variantMap.get(String(variantId));
      if (!variant) return;

      const selection = this._slotSelectionDefaults(variant);
      if (this._subscriptionsMode === "show") {
        selection.purchaseType = this._getActivePurchaseType();
        if (selection.purchaseType === "subscribe") {
          selection.sellingPlanId = this._getFirstPlanId(variantId);
        }
      }
      this._slotSelections[this._activeSlotIndex] = selection;
      this._renderSlot(this._activeSlotIndex);

      const nextEmpty = this._findFirstEmptySlotIndex();
      if (nextEmpty !== this._activeSlotIndex) this._setActiveSlot(nextEmpty);
      this._syncDropdownForActiveSlot();
      this._syncSellingPlans();
      this._syncSubscriptionUi();
      this._syncAddonPrices();
      this._emitChange();
    }

    _readQtyValue() {
      const valueEl = this._qtyPicker?.querySelector("[data-addon-qty-value]");
      return parseInt(valueEl?.textContent, 10) || 0;
    }

    _getQtyBounds() {
      const control = this._qtyPicker?.querySelector("[data-addon-qty-control]");
      return {
        min: parseInt(control?.dataset.addonQtyMin || "1", 10),
        max: parseInt(control?.dataset.addonQtyMax || String(this._multiMax), 10)
      };
    }

    _handleQtyClick(event) {
      const decreaseBtn = event.target.closest("[data-addon-qty-decrease]");
      const increaseBtn = event.target.closest("[data-addon-qty-increase]");
      if (!decreaseBtn && !increaseBtn) return;
      if (!this._qtyPicker?.contains(event.target)) return;

      event.preventDefault();
      event.stopPropagation();

      const { min, max } = this._getQtyBounds();
      const nextQty = decreaseBtn
        ? Math.max(min, this._currentQty - 1)
        : Math.min(max, this._currentQty + 1);

      if (nextQty === this._currentQty) return;
      this._setQty(nextQty);
    }

    _setQty(nextQty) {
      if (nextQty < this._currentQty) {
        for (let i = nextQty; i < this._multiMax; i += 1) {
          this._slotSelections[i] = null;
        }
      }

      this._currentQty = nextQty;
      if (this._activeSlotIndex >= this._currentQty) {
        this._activeSlotIndex = Math.max(0, this._currentQty - 1);
      }

      this._syncQtyPicker();
      this._syncSlotVisibility();
      this._renderAllSlots();
      this._setActiveSlot(this._activeSlotIndex);
      this._syncDropdownForActiveSlot();
      this._syncSellingPlans();
      this._syncSubscriptionUi();
      this._syncAddonPrices();
      this._emitChange();
    }

    _syncQtyPicker() {
      const control = this._qtyPicker?.querySelector("[data-addon-qty-control]");
      const valueEl = this._qtyPicker?.querySelector("[data-addon-qty-value]");
      const decreaseBtn = this._qtyPicker?.querySelector("[data-addon-qty-decrease]");
      const increaseBtn = this._qtyPicker?.querySelector("[data-addon-qty-increase]");
      if (!control || !valueEl) return;

      const { min, max } = this._getQtyBounds();
      valueEl.textContent = String(this._currentQty);
      if (decreaseBtn) decreaseBtn.disabled = this._currentQty <= min;
      if (increaseBtn) increaseBtn.disabled = this._currentQty >= max;
    }

    _syncSlotVisibility() {
      this.querySelectorAll("[data-addon-slot]").forEach((slotEl) => {
        const index = parseInt(slotEl.dataset.slotIndex, 10);
        slotEl.hidden = index > this._currentQty;
      });
    }

    _setActiveSlot(index) {
      this._activeSlotIndex = index;
      this.querySelectorAll("[data-addon-slot]").forEach((slotEl) => {
        const slotIndex = parseInt(slotEl.dataset.slotIndex, 10) - 1;
        slotEl.classList.toggle("purchase-options__addon-slot--active", slotIndex === index && !slotEl.hidden);
      });
    }

    _clearSlot(index) {
      this._slotSelections[index] = null;
      this._renderSlot(index);
      this._setActiveSlot(index);
      this._syncDropdownForActiveSlot();
      this._syncSellingPlans();
      this._syncSubscriptionUi();
      this._syncAddonPrices();
      this._emitChange();
    }

    _renderAllSlots() {
      for (let i = 0; i < this._multiMax; i += 1) {
        this._renderSlot(i);
      }
    }

    _buildSlotImageHtml(selection) {
      const styleAttr = selection.imageWrapStyle
        ? ` style="${selection.imageWrapStyle.replace(/"/g, "&quot;")}"`
        : "";
      const alt = (selection.title || "").replace(/"/g, "&quot;");
      const imageHtml = selection.imageUrl
        ? `<img class="oil-selection-card__image" src="${selection.imageUrl}" alt="${alt}" width="128" height="128" loading="lazy">`
        : `<div class="oil-selection-card__image-placeholder" aria-hidden="true"></div>`;

      return `<div class="oil-selection-card__image-wrap purchase-options__addon-slot-image-wrap"${styleAttr}>${imageHtml}</div>`;
    }

    _renderSlot(index) {
      const slotEl = this.querySelector(`[data-addon-slot][data-slot-index="${index + 1}"]`);
      if (!slotEl) return;

      const selection = this._slotSelections[index];
      const trigger = slotEl.querySelector("[data-addon-slot-trigger]");
      const labelEl = slotEl.querySelector("[data-addon-slot-label]") || slotEl.querySelector(".purchase-options__addon-slot-label");
      let removeBtn = slotEl.querySelector("[data-addon-slot-remove]");

      if (selection?.id) {
        slotEl.classList.add("purchase-options__addon-slot--filled");
        slotEl.dataset.variantId = String(selection.id);
        trigger.innerHTML = this._buildSlotImageHtml(selection);
        trigger.setAttribute("aria-label", selection.title);

        if (labelEl) {
          labelEl.textContent = selection.title;
          labelEl.hidden = false;
          labelEl.classList.remove("purchase-options__addon-slot-label--hidden");
        } else {
          const label = document.createElement("p");
          label.className = "purchase-options__addon-slot-label";
          label.textContent = selection.title;
          slotEl.appendChild(label);
        }

        if (!removeBtn) {
          removeBtn = document.createElement("button");
          removeBtn.type = "button";
          removeBtn.className = "purchase-options__addon-slot-remove";
          removeBtn.setAttribute("data-addon-slot-remove", "");
          removeBtn.setAttribute("aria-label", `Remove ${selection.title}`);
          removeBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M1 1 9 9M9 1 1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;
          slotEl.appendChild(removeBtn);
        } else {
          removeBtn.setAttribute("aria-label", `Remove ${selection.title}`);
        }
      } else {
        slotEl.classList.remove("purchase-options__addon-slot--filled");
        delete slotEl.dataset.variantId;
        const placeholder = `Choose ${ordinal(index)} Oil`;
        trigger.innerHTML = `<span class="purchase-options__addon-slot-empty"><span class="purchase-options__addon-slot-plus" aria-hidden="true">+</span><span class="purchase-options__addon-slot-placeholder">${placeholder}</span></span>`;
        trigger.setAttribute("aria-label", placeholder);

        if (labelEl) {
          labelEl.textContent = "";
          labelEl.hidden = true;
          labelEl.classList.add("purchase-options__addon-slot-label--hidden");
        }
        removeBtn?.remove();
      }
    }

    _setDropdownVariant(variantId) {
      if (!this._cardDropdown) return;

      const input = this._cardDropdown.querySelector("[data-purchase-option-addon-input]");
      const selectedCard = this._cardDropdown.querySelector(".card-dropdown__selected-card");
      const options = this._cardDropdown.querySelectorAll(".card-dropdown__option");
      const id = String(variantId || "");
      let matched = null;

      options.forEach((option) => {
        const isSelected = id !== "" && option.dataset.value === id;
        option.classList.toggle("card-dropdown__option--selected", isSelected);
        option.setAttribute("aria-selected", isSelected ? "true" : "false");
        if (isSelected) matched = option;
      });

      if (input) input.value = id;
      if (selectedCard) {
        selectedCard.innerHTML = matched ? matched.innerHTML : "";
      }
    }

    _ensureSingleAddonSelected() {
      const input = this.querySelector("[data-purchase-option-addon-input]");
      if (!this._cardDropdown) return;

      if (input?.value) {
        this._selectedVariantId = input.value;
        this._setDropdownVariant(input.value);
        return;
      }

      const variantId = this._getFirstAvailableDropdownVariantId();
      if (!variantId) return;

      this._selectedVariantId = variantId;
      if (input) input.value = variantId;
      this._setDropdownVariant(variantId);
    }

    _handleAddonChange(event) {
      const variantId = event.detail?.variantId || "";
      if (!variantId) return;

      this._selectedVariantId = variantId;
      this._syncSellingPlans();
      this._syncSubscriptionUi();
      this._syncAddonPrices();
      if (this.selected) this._emitChange();
    }

    _syncAddonVisibility() {
      const visible = this.selected;

      if (this._qtyPicker) {
        this._qtyPicker.hidden = !visible || !this._showAddQuantityPicker;
      }

      if (this._addonMulti) {
        this._addonMulti.hidden = !visible;
      } else if (this._addonSelect) {
        this._addonSelect.hidden = !visible;
      }

      if (this._optionForm) {
        this._optionForm.hidden = !visible;
      }

      if (this._optionFormOld) {
        this._optionFormOld.hidden = !visible;
      }

      if (visible) {
        this._syncFormOldRuntime();
      }

      if (this._subscriptionControls) {
        this._subscriptionControls.hidden = !visible;
      }

      this._syncSubscriptionUi();
    }

    _syncFormOldRuntime() {
      if (this.dataset.optionType !== "lead_form" || !this._optionFormOld) return;

      const root = this._optionFormOld;
      const stripeId = root.dataset.stripeOnboardingId || "";
      if (stripeId) {
        window.stripePriceVariantId = stripeId;
      }

      const paymentUrl = root.dataset.paymentUrl || "";
      if (paymentUrl) {
        window.dynamicFormPaymentUrlOverride = paymentUrl;
      }

      const submissionFlow = root.dataset.submissionFlow || "";
      const callbackFlow = root.dataset.callbackFlow || "";
      const affiliateProductId = root.dataset.affiliateProductId || "";

      // Only wire ATC callback for invoke-callback flows. Step-form / Stripe
      // callbacks are owned by dynamic-form-v2 (form_callback_action).
      const leadFormSection = document.querySelector(".aroma-dynamic-lead-form-wrapper");
      const usesStepFormCallback = Boolean(
        leadFormSection?.querySelector("[data-lead-form-steps], .lead-form__step-container")
      );

      if (
        !usesStepFormCallback &&
        (submissionFlow === "SUBMISSION_W_MAIN_PRODUCT" ||
          submissionFlow === "SUBMISSION_W_DUPLICATE_PRODUCT")
      ) {
        window.submissionCallBack = async () => {
          const panel = this.closest(".product-purchase-panel");
          const mainVariantId =
            panel?.querySelector('input[name="id"]')?.value ||
            this.closest("purchase-options-component")?.dataset.mainVariantId ||
            "";
          const variantId =
            submissionFlow === "SUBMISSION_W_DUPLICATE_PRODUCT" && affiliateProductId
              ? affiliateProductId
              : mainVariantId;

          if (!variantId) return;

          await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : "/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
          });

          if (callbackFlow === "GO_TO_CHECKOUT") {
            window.location.href = "/checkout";
          }
        };
      }
    }

    _handleChange() {
      if (!this.selected) {
        this.clearSelection();
      } else {
        this._syncAddonVisibility();

        if (this._isMultiSelect) {
          if (this.shouldPreselect && !this._hasAnySlotSelection()) {
            this._applyPreselectSlots();
          }
          this._setActiveSlot(this._findFirstEmptySlotIndex());
          this._syncDropdownForActiveSlot();
          this._syncSellingPlans();
        } else {
          this._ensureSingleAddonSelected();
          this._syncSellingPlans();
        }

        this._syncSubscriptionUi();
      }

      this._syncAddonPrices();
      this._emitChange();
    }

    _emitChange() {
      const pricing = this._getSelectionPricing();
      const pricingTotals = this._isMultiSelect ? this._pricingTotals(pricing) : this._pricingSummary(pricing.items[0] || {});

      emit(this, "purchase-option:change", {
        optionId: this.optionId,
        selected: this.selected,
        selectedVariantId: this.selectedVariantId,
        selectedVariantIds: this.selectedVariantIds,
        selectionItem: this.selectionItem,
        discountedPrice: pricingTotals.discountedPrice,
        originalPrice: pricingTotals.originalPrice,
        compareAtPrice: pricingTotals.compareAtPrice,
        mainProductDiscount: this._getMainProductDiscountContext()
      });
    }
  }

  class PurchaseOptionsComponent extends HTMLElement {
    connectedCallback() {
      this._options = Array.from(this.querySelectorAll("purchase-option-component"));
      this._onOptionChange = this._handleOptionChange.bind(this);
      this._onOptionPriceChange = this._handleOptionChange.bind(this);
      this.addEventListener("purchase-option:change", this._onOptionChange);
      this.addEventListener("purchase-option:price-change", this._onOptionPriceChange);
      this._emitChange();
      emit(this, "purchase-options:ready", this._detail());
    }

    disconnectedCallback() {
      this.removeEventListener("purchase-option:change", this._onOptionChange);
      this.removeEventListener("purchase-option:price-change", this._onOptionPriceChange);
    }

    _handleOptionChange(event) {
      if (!this.mandatory && event.detail.selected) {
        this._options.forEach((option) => {
          if (option.optionId !== event.detail.optionId) option.selected = false;
        });
      }

      this._options.forEach((option) => {
        if (!option.selected) option.clearSelection();
      });

      this._emitChange();
    }

    get mandatory() {
      return parseBool(this.dataset.mandatory);
    }

    get hasSelectedFormOption() {
      return this._options.some((option) => {
        if (!option.selected) return false;
        const type = option.dataset.optionType;
        return type === "form_purchase_option" || type === "lead_form";
      });
    }

    get selectedOptions() {
      return this._options
        .filter((option) => option.selected)
        .map((option) => option.selectionItem)
        .filter(Boolean);
    }

    get selection() {
      const selectedOptionIds = this.selectedOptions.map((item) => item.optionId);
      const mainVariantId = this.dataset.mainVariantId || "";
      const mainLine = mainVariantId ? { id: mainVariantId, quantity: 1 } : null;
      const activeOption = this.selectedOptions[0] || null;
      const mainProductDiscount = activeOption?.mainProductDiscount || null;
      const promotionalSource = activeOption?.promotionalSource || null;
      const addonTotals = this.selectedOptions.reduce(
        (totals, item) => ({
          discountedPrice: totals.discountedPrice + (Number(item.discountedPrice) || 0),
          originalPrice: totals.originalPrice + (Number(item.originalPrice) || 0),
          compareAtPrice: totals.compareAtPrice + (Number(item.compareAtPrice) || 0)
        }),
        { discountedPrice: 0, originalPrice: 0, compareAtPrice: 0 }
      );

      return {
        productId: this.dataset.productId || "",
        mainVariantId,
        mandatory: this.mandatory,
        selectedOptionIds,
        selectedOptions: this.selectedOptions,
        hasSelectedFormOption: this.hasSelectedFormOption,
        mainProductDiscount,
        promotionalSource,
        discountedPrice: addonTotals.discountedPrice,
        originalPrice: addonTotals.originalPrice,
        compareAtPrice: addonTotals.compareAtPrice,
        cartLines: mainLine ? [mainLine] : []
      };
    }

    _detail() {
      return {
        productId: this.dataset.productId || "",
        mainVariantId: this.dataset.mainVariantId || "",
        selection: this.selection,
        cartLines: this.selection.cartLines
      };
    }

    validateForCart() {
      if (this.mandatory && this.selectedOptions.length === 0) {
        return { ok: false, message: "Please select a purchase option." };
      }

      for (const option of this._options) {
        if (!option.selected) continue;
        const optionType = option.dataset.optionType || "";
        if (optionType === "form_purchase_option" || optionType === "lead_form") {
          continue;
        }

        const item = option.selectionItem;
        if (!item) {
          return { ok: false, message: "Please complete your purchase option selections." };
        }

        if (item.addonSlots) {
          const requiredCount = item.quantity || 0;
          if (item.addonSlots.length < requiredCount) {
            return { ok: false, message: "Please complete all add-on selections." };
          }
          continue;
        }

        if (!item.variantId) {
          return { ok: false, message: "Please select an add-on." };
        }
      }

      return { ok: true, message: "" };
    }

    buildAddonCartLines(bundleId) {
      const lines = [];
      const withLineProperties = (line, compareAtCents, priceCents, promotionalSource) => ({
        ...line,
        properties: {
          ...buildCompareAtProperties(compareAtCents, priceCents),
          ...(bundleId ? { _bundle_id: bundleId } : {}),
          ...(promotionalSource ? { _promotional_source: promotionalSource } : {})
        }
      });

      for (const item of this.selectedOptions) {
        const promotionalSource = parsePromotionalSource(item.promotionalSource);
        if (item.addonSlots?.length) {
          item.addonSlots.forEach((slot) => {
            if (!slot.variantId) return;
            const line = {
              id: Number(slot.variantId),
              quantity: 1
            };
            if (slot.sellingPlanId) line.selling_plan = Number(slot.sellingPlanId);
            const compareAt = Number(slot.compareAtPrice) || 0;
            // Native variant price only — not the post-discount unit.
            const price = Number(slot.originalPrice) || 0;
            lines.push(withLineProperties(line, compareAt, price, promotionalSource));
          });
          continue;
        }

        if (!item.variantId) continue;
        const line = {
          id: Number(item.variantId),
          quantity: 1
        };
        if (item.sellingPlanId) line.selling_plan = Number(item.sellingPlanId);
        const compareAt = Number(item.compareAtPrice) || 0;
        // Native variant price only — not the post-discount unit.
        const price = Number(item.originalPrice) || 0;
        lines.push(withLineProperties(line, compareAt, price, promotionalSource));
      }

      return lines;
    }

    _emitChange() {
      const detail = this._detail();
      this.dataset.selection = JSON.stringify(detail.selection);
      emit(this, "purchase-options:change", detail);
    }
  }

  customElements.define("purchase-option-component", PurchaseOptionComponent);
  customElements.define("purchase-options-component", PurchaseOptionsComponent);
})();
